import { ElementOptionStyle } from '@/components/chart/chart-tooltip/chart-tooltip.types';

export enum FILTER_TYPE {
  CHECKBOX_WITH_OPTIONS = 'CHECKBOX_WITH_OPTIONS',
  SEARCH_CHECKBOX_WITH_OPTIONS = 'SEARCH_CHECKBOX_WITH_OPTIONS',
  CHECKBOX_WITHOUT_OPTIONS = 'CHECKBOX_WITHOUT_OPTIONS',
  DATE_RANGE = 'SINGLE_RANGE',
  SINGLE_DATE = 'SINGLE_DATE',
  MULTI_DATE_RANGE = 'MULTI_RANGE',
  LEAD_TIME = 'LEAD_TIME',
  DYNAMIC_TIME_SLIDER = 'DYNAMIC_TIME_SLIDER',
}

export enum GROUP_NAME {
  PERIOD_AT_TIME = 'PERIOD OF TIME',
  GENERAL = 'GENERAL',
  COMPSET = 'COMPSET',
  STATUS = 'STATUS',
  FUNCTION = 'FUNCTION'
}

export enum TAB {
  SELECTIVE = 'selective',
  RANGE = 'range',
  OUTLET = 'outlet',
  SEGMENTS = 'segments',
  STATUS = 'status',
  FUNCTION = 'function',
  GENERAL = 'general',
  PROFILE = 'profile'
}

export enum FILTER {
  YEAR = 'year',
  QUARTER = 'quarter',
  SEASON = 'season',
  MONTH = 'month',
  DAY_OF_WEEK = 'dayOfWeek',
  RESERVATION_DATE = 'reservationDate',
  MAIN_MARKET_SEGMENT = 'mainMarketSegment',
  EXTERNAL_ID = 'externalId',
  PRODUCT_TYPE = 'productType',
  RATE_CODE = 'rateCode',
  MARKET_SEGMENT = 'marketSegment',
  MARKET = 'market',
  REVENUE_CENTER = 'revenueCenter',
  REVENUE_GROUP = 'revenueGroup',
  REVENUE_OUTLET = 'revenueOutlet',
  REVENUE_TYPE = 'revenueType',
  RESERVATION_STATUS = 'reservationStatus',
  LEAD_TIME = 'leadTime',
  BUSINESS_DATE = 'businessDate',
  LOS = 'los',
  RML = 'rml',
  PRODUCT = 'product',
  HOTEL_NAME = 'hotelName',
  COMPANY_NAME = 'companyName',
  COMPANY_ID = 'companyId',
  AGENT_NAME = 'agentName',
  CHANNEL_NAME = 'channelName',
  GUESTS_PROFILE_NUMBER = 'guestsProfileNumber',
  COUNTRY_CODE = 'countryCode',
  RESERVATION_ID = 'reservationId'
}

export type Filter = CheckboxWithOptions | SearchCheckboxWithOptions | CheckboxWithoutOptions | DateRange | MultiDateRange | LeadTime | SingleDate;
export type FilterGroup = CheckboxWithOptions | SearchCheckboxWithOptions | MultiDateRange | LeadTime;
export type FilterValueRange = { min: string; max: string }
export type SelectedFiltersValues = Array<boolean | string | FilterValueRange>
export type SelectedFilterValue = string | ElementOptionStyle | boolean | FilterValueRange | Range


export interface FilterInfo {
  filterCounter: number;
  groupNamesPresence: string[];
  tabNamesPresence: string[];
}

export type RequestFilter = Partial<SelectedFilter>

export interface SelectedFilterLabelObject {
  originalName: string;
  label: string | null;
}

export interface SelectedFilter {
  columnName: string;
  type: FILTER_TYPE | undefined;
  values: Array<SelectedFilterValue>;
  labels?: SelectedFilterLabelObject[];
  exclusive?: boolean;
  groupName: string;
  tabName: string;
  isLead?: boolean;
  uuid: string;
}

export interface FilterOption extends SelectedFilter {
  valueName: SelectedFilterValue;
  valueLabel?: string | SelectedFilterValue | undefined;
}

export interface LeadFilterDetails {
  groupName: GROUP_NAME | string;
  tabName: TAB | string;
  name: string;
}

export interface FilterPanelState {
  disabledFilters: FilterOption[];
  selectedFilters: SelectedFilter[];
  filtersAppliedOnChart: SelectedFilter[];
  copyOfCurrentTabContentForStyleConfiguration: TabContent;
  currentLeadFilter: LeadFilterDetails;
  selectedFunctions: string[];
  appliedTabs: string[];
  groups: Group[];
  isHideDropdownOnClick: boolean;
}

export interface Group {
  groupName: GROUP_NAME;
  tabs: Tab[];
}

export interface Tab {
  tabName: string;
  isEditMode: boolean;
  isApplied?: boolean;
  isTracked?: boolean;
  content: TabContent;

  [ k: string ]: any;
}

export type TabContent = (CheckboxWithOptions | SearchCheckboxWithOptions | CheckboxWithoutOptions | DateRange | MultiDateRange | LeadTime | SingleDate)[];

export interface BaseFilter<T> {
  uuid: string;
  name: string;
  label: string | null;
  isLead?: boolean;
  isExclusive?: boolean;
  contentType: T;

  [k: string]: any;
}

export interface FilterStylesObject {
  functions: {
    [ k: string ]: {
      label: string | null;
      color?: string | null;
      isDefault: boolean;
      order?: number | null;
    };
  };
  filters: {
    [k: string]: {
      label: string | null;
      options: FilterStyleOption;
    };
  };
}

export interface FilterStyleOption {
  name: string;
  label: string | null;
  isDefault: boolean;
  color?: string | null;
  order?: number | null;
}

export interface CheckboxWithOptions extends BaseFilter<FILTER_TYPE.CHECKBOX_WITH_OPTIONS> {
  values: CheckboxWithOptionsValue[];
  color?: string | null;
}

export interface SearchCheckboxWithOptions extends BaseFilter<FILTER_TYPE.SEARCH_CHECKBOX_WITH_OPTIONS> {
  values: CheckboxWithOptionsValue[];
  color?: string | null;
}

export interface CheckboxWithOptionsValue {
  name: string;
  label: string | null;
  isSelected: boolean;
  isDefault?: boolean;
  color?: string | null;
  order?: number | null;

  disabled?: boolean;
  [k: string]: any;
}

export interface CheckboxWithoutOptions extends BaseFilter<FILTER_TYPE.CHECKBOX_WITHOUT_OPTIONS> {
  isSelected: boolean;
  isDefault: boolean;
  color?: string | null;
  order?: number | null;
}

export interface Range {
  from: string;
  to: string;
}

export interface OneDate {
  value: string;
}

export interface MultiDateRange extends BaseFilter<FILTER_TYPE.MULTI_DATE_RANGE> {
  values: SelectiveRange[];
  defaultValues: Range[];
}

export type LeadTime = BaseFilter<FILTER_TYPE.LEAD_TIME> & Range
export type DateRange = BaseFilter<FILTER_TYPE.DATE_RANGE> & Range
export type SingleDate = BaseFilter<FILTER_TYPE.SINGLE_DATE> & OneDate;

export interface SelectiveRange extends Range {
  isSelected: boolean;
  disabled?: boolean;
}

export interface GroupsRequestParams {
  uuid: string;
  reportType: string;
}

export interface TrackedTab {
  tab: string;
  group: string;
}

export interface FilterPanelLabels {
  name: string;
  label: string;
}
